// ./src/components/features/Events/EventCard/EventCard.tsx
//
// One row on the All Events page. Clickable → opens the event dashboard. Shows the
// occasion emoji, title/subtitle, when, format, how guests join, and a lifecycle status badge — and, on an unpublished
// event, Remove.

import React from 'react';
import { ActionIcon, Card, Group, Stack, Text, Badge, ThemeIcon, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { AppIcon } from '@/components/generic';
import { Event } from '@/utils/api';
import { useOptionalEventSetupCatalogContext } from '@/contexts/EventSetupCatalogContext';
import { EventModeBadge } from '../EventModeBadge';
import { EVENT_SCHEDULE_BADGES, eventFormatLabel, eventRemovable, eventStatusMeta, eventWhenText } from '../eventListHelpers';

export interface EventCardProps {
  event: Event;
  onOpen: (event: Event) => void;
  /**
   * Remove this event (2026-10-04 — owner: "let user remove unpublished events"). Offered only when the server grants
   * `remove` on it, which it does for an unpublished one alone.
   */
  onRemove?: (event: Event) => void;
  /** #116 — copy this event (a new draft). The list passes it only for the account's own events. */
  onCopy?: (event: Event) => void;
  /** Its removal is in flight — the button spins. */
  removing?: boolean;
  /**
   * Merged into the card's own style. ⚠ `SwipeableCard` squares the revealed side by cloning its child with a
   * `borderRadius` here — a card that dropped this prop kept its rounded corner against the action buttons.
   */
  style?: React.CSSProperties;
}

export const EventCard: React.FC<EventCardProps> = ({ event, onOpen, onRemove, onCopy, removing = false, style }) => {
  const { t } = useTranslation(['events', 'common']);
  // #62 — the type (and its emoji) from the one event-type catalog; null while it loads.
  const type = useOptionalEventSetupCatalogContext()?.findType(event.eventCategoryId) ?? null;
  const status = eventStatusMeta(event.eventStatus);
  const scheduleBadge = event.scheduleMode ? EVENT_SCHEDULE_BADGES[event.scheduleMode] : undefined;
  const whenText = eventWhenText(event, t);
  const formatLabel = eventFormatLabel(event);

  return (
    <Card
      withBorder
      radius="md"
      padding="md"
      onClick={() => onOpen(event)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(event);
        }
      }}
      style={{ cursor: 'pointer', opacity: removing ? 0.5 : 1, ...style }}
    >
      <Group gap="sm" wrap="nowrap" align="center">
        <ThemeIcon variant="light" color="brand" size={44} radius="md" style={{ flexShrink: 0, fontSize: 22 }}>
          <span aria-hidden>{type?.emoji ?? '📅'}</span>
        </ThemeIcon>

        <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
          <Group gap="xs" wrap="nowrap" justify="space-between">
            <Text fw={600} truncate style={{ minWidth: 0 }}>
              {event.title || t('events:list.untitled', { defaultValue: 'Untitled event' })}
            </Text>
            <Badge size="sm" variant="light" color={status.color} style={{ flexShrink: 0 }}>
              {status.label}
            </Badge>
          </Group>

          {event.subtitle && (
            <Text size="sm" c="dimmed" truncate>
              {event.subtitle}
            </Text>
          )}

          <Group gap="xs" wrap="wrap">
            <Group gap={4} wrap="nowrap">
              <AppIcon iconName="Calendar" size={13} />
              <Text size="xs" c="dimmed">
                {whenText}
              </Text>
            </Group>
            {formatLabel && (
              <Group gap={4} wrap="nowrap">
                <AppIcon iconName="MapPin" size={13} />
                <Text size="xs" c="dimmed">
                  {formatLabel}
                </Text>
              </Group>
            )}
            {/* How guests join (2026-10-07) — save the date / invite list / open RSVP / tickets. */}
            <EventModeBadge eventMode={event.eventMode} size="sm" />
            {scheduleBadge && (
              <Badge size="xs" variant="outline" color="grape">
                {scheduleBadge}
              </Badge>
            )}
          </Group>
        </Stack>

        {onCopy && (
          <Tooltip label={t('events:list.copy', { defaultValue: 'Copy event' })}>
            <ActionIcon
              variant="subtle"
              color="neutral"
              size="lg"
              aria-label={t('events:list.copy', { defaultValue: 'Copy event' })}
              // ⚠ The card itself opens the event: the button must not.
              onClick={(e) => {
                e.stopPropagation();
                onCopy(event);
              }}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <AppIcon iconName="Copy" size={18} />
            </ActionIcon>
          </Tooltip>
        )}
        {onRemove && eventRemovable(event) && (
          <Tooltip label={t('events:list.remove', { defaultValue: 'Remove event' })}>
            <ActionIcon
              variant="subtle"
              color="red"
              size="lg"
              loading={removing}
              aria-label={t('events:list.remove', { defaultValue: 'Remove event' })}
              // ⚠ The card itself opens the event: the button must not.
              onClick={(e) => {
                e.stopPropagation();
                onRemove(event);
              }}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <AppIcon iconName="Trash" size={18} />
            </ActionIcon>
          </Tooltip>
        )}
        <AppIcon iconName="ChevronRight" size={18} />
      </Group>
    </Card>
  );
};
